import React, { useEffect, useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, FlaskConical, Beaker, Target, Trophy, X } from 'lucide-react';
import { getEnabledModes } from '../modes';
import { LabStation } from './LabStation';
import { MissionBriefing } from './MissionBriefing';
import { FamilyPicker, type FamilyPickerMode } from './FamilyPicker';
import { ChallengeAcceptModal } from './ChallengeAcceptModal';
import { FluencyIntro } from './FluencyIntro';
import { SprintIntro } from './SprintIntro';
import { FreePracticeIntro } from './FreePracticeIntro';
import { ChallengeIntro } from './ChallengeIntro';
import { FluencyModulePicker } from './FluencyModulePicker';
import { PageContainer } from '../components/shared/PageContainer';
import { itemVariants } from '../components/shared/animations';
import { WorldZone } from '../components/shared/WorldZone';
import { DjayAvatar, useDjaySelection, getDjayLabWelcome } from '../mascot';
import { useAppStore } from '../app/hooks/useAppStore';
import { getFamilyMasteryKey, getWeakestFamiliesGlobal } from '../mastery';
import { dataLayer, type PersonalRecords } from '../data/dataLayer';
import { isFluencyColdStart as checkFluencyColdStart } from '../modes';
import { humanizeFamilyId } from '../content';
// ─── Types ────────────────────────────────────────────────────
interface StartModeConfig {
  targetFamilyId?: string;
  targetModules?: string[];
}
interface LearningLabPageProps {
  onStartMode?: (modeId: string, config?: StartModeConfig) => void;
}
// ─── Component ────────────────────────────────────────────────
export function LearningLabPage({
  onStartMode
}: LearningLabPageProps) {
  const allModes = getEnabledModes();
  const {
    selectedVariant
  } = useDjaySelection();
  const {
    state,
    selectedGroup
  } = useAppStore();
  const [lastSession, setLastSession] = useState<{
    modeId: string;
    familyId?: string;
    timestamp: number;
    score?: number;
  } | null>(null);
  const [newlyUnlockedFamilyId, setNewlyUnlockedFamilyId] = useState<string | null>(null);
  const [personalRecords, setPersonalRecords] = useState<PersonalRecords>({});
  useEffect(() => {
    dataLayer.getLastOefenlabSession().then(session => {
      if (session) {
        setLastSession(session);
      }
    }).catch(() => {});
    dataLayer.getNewlyUnlockedChallenge().then(familyId => {
      if (familyId) {
        setNewlyUnlockedFamilyId(familyId);
        dataLayer.clearNewlyUnlockedChallenge();
      }
    }).catch(() => {});
    dataLayer.getPersonalRecords().then(records => {
      setPersonalRecords(records);
    }).catch(() => {});
  }, []);
  const labWelcome = useMemo(() => getDjayLabWelcome(lastSession), [lastSession]);
  // ── FamilyPicker state ──
  const [familyPickerOpen, setFamilyPickerOpen] = useState(false);
  const [familyPickerMode, setFamilyPickerMode] = useState<FamilyPickerMode>('sprint');
  const [pendingModeId, setPendingModeId] = useState<string | null>(null);
  // ── ChallengeAcceptModal state ──
  const [challengeModalOpen, setChallengeModalOpen] = useState(false);
  const [pendingChallengeFamilyId, setPendingChallengeFamilyId] = useState<string | null>(null);
  // ── FluencyIntro state ──
  const [fluencyIntroOpen, setFluencyIntroOpen] = useState(false);
  const [pendingFluencyConfig, setPendingFluencyConfig] = useState<{
    familyId?: string;
  } | null>(null);
  // ── FluencyModulePicker state ──
  const [fluencyModulePickerOpen, setFluencyModulePickerOpen] = useState(false);
  // ── SprintIntro state ──
  const [sprintIntroOpen, setSprintIntroOpen] = useState(false);
  const [pendingSprintConfig, setPendingSprintConfig] = useState<{
    familyId?: string;
  } | null>(null);
  // ── FreePracticeIntro state ──
  const [freePracticeIntroOpen, setFreePracticeIntroOpen] = useState(false);
  const [pendingFreePracticeConfig, setPendingFreePracticeConfig] = useState<{
    familyId?: string;
  } | null>(null);
  // ── ChallengeIntro state ──
  const [challengeIntroOpen, setChallengeIntroOpen] = useState(false);
  // ── Personalization stats ──
  const groupSuffix = `_g${selectedGroup}`;
  const fluencyFamilies = useMemo(() => {
    return Object.entries(state.familyMastery || {}).filter(([key, s]) => key.endsWith(groupSuffix) && s.masteryScore >= 50 && s.masteryScore < 80);
  }, [state.familyMastery, groupSuffix]);
  const fluencyCount = fluencyFamilies.length;
  const fluencyMastery = useMemo(() => {
    if (fluencyCount === 0) return 0;
    return Math.round(fluencyFamilies.reduce((acc, [_, s]) => acc + s.masteryScore, 0) / fluencyCount);
  }, [fluencyFamilies, fluencyCount]);
  const challengeFamilies = useMemo(() => {
    return Object.entries(state.familyMastery || {}).filter(([key, s]) => key.endsWith(groupSuffix) && s.masteryScore >= 70);
  }, [state.familyMastery, groupSuffix]);
  const challengeCount = challengeFamilies.length;
  const challengeMastery = useMemo(() => {
    if (challengeCount === 0) return 0;
    return Math.round(challengeFamilies.reduce((acc, [_, s]) => acc + s.masteryScore, 0) / challengeCount);
  }, [challengeFamilies, challengeCount]);
  const weakestFamilyData = useMemo(() => {
    const weakest = getWeakestFamiliesGlobal(state.familyMastery || {}, selectedGroup, 1);
    return weakest.length > 0 ? weakest[0] : null;
  }, [state.familyMastery, selectedGroup]);
  const overallMastery = useMemo(() => {
    const families = Object.entries(state.familyMastery || {}).filter(([key, s]) => key.endsWith(groupSuffix) && s.attempts > 0);
    if (families.length === 0) return 0;
    const sum = families.reduce((acc, [_, s]) => acc + s.masteryScore, 0);
    return Math.round(sum / families.length);
  }, [state.familyMastery, groupSuffix]);
  const getEnrichmentForMode = (modeId: string) => {
    switch (modeId) {
      case 'fluency':
        return {
          statLabel: fluencyCount > 0 ? `${fluencyCount} onderwerpen klaar` : 'Train je snelheid',
          masteryPercent: fluencyCount > 0 ? fluencyMastery : undefined,
          personalRecord: personalRecords.fluency?.longestStreak ? `Record: ${personalRecords.fluency.longestStreak} op rij` : undefined
        };
      case 'sprint':
        return {
          statLabel: weakestFamilyData ? `${humanizeFamilyId(weakestFamilyData.familyId)}` : 'Oefen je zwakste onderwerpen',
          masteryPercent: weakestFamilyData ? weakestFamilyData.masteryScore : undefined,
          personalRecord: personalRecords.sprint?.bestScore ? `Beste score: ${personalRecords.sprint.bestScore}%` : undefined
        };
      case 'challenge':
        return {
          statLabel: challengeCount > 0 ? `${challengeCount} onderwerpen klaar` : 'Test je kennis',
          masteryPercent: challengeCount > 0 ? challengeMastery : undefined,
          personalRecord: personalRecords.challenge?.bestScore ? `Beste score: ${personalRecords.challenge.bestScore}%` : undefined
        };
      case 'free':
        return {
          statLabel: 'Alle vakken beschikbaar',
          masteryPercent: overallMastery > 0 ? overallMastery : undefined
        };
      default:
        return {};
    }
  };
  // ── Handlers ──
  const handleModeClick = (modeId: string, familyId?: string) => {
    if (modeId === 'fluency') {
      dataLayer.getFluencyIntroSeen().then(seen => {
        if (!seen) {
          setPendingFluencyConfig({
            familyId
          });
          setFluencyIntroOpen(true);
        } else {
          if (familyId && onStartMode) {
            onStartMode('fluency', {
              targetFamilyId: familyId
            });
          } else {
            setFluencyModulePickerOpen(true);
          }
        }
      }).catch(() => {});
      return;
    }
    if (modeId === 'sprint') {
      dataLayer.getSprintIntroSeen().then(seen => {
        if (!seen) {
          setPendingSprintConfig({
            familyId
          });
          setSprintIntroOpen(true);
        } else {
          proceedWithSprint(familyId);
        }
      }).catch(() => {});
      return;
    }
    if (modeId === 'free') {
      dataLayer.getFreePracticeIntroSeen().then(seen => {
        if (!seen) {
          setPendingFreePracticeConfig({
            familyId
          });
          setFreePracticeIntroOpen(true);
        } else {
          proceedWithFreePractice(familyId);
        }
      }).catch(() => {});
      return;
    }
    if (modeId === 'challenge') {
      dataLayer.getChallengeIntroSeen().then(seen => {
        if (!seen) {
          setChallengeIntroOpen(true);
        } else {
          proceedWithChallenge(familyId);
        }
      }).catch(() => {});
      return;
    }
    if (familyId && onStartMode) {
      onStartMode(modeId, {
        targetFamilyId: familyId
      });
      return;
    }
    onStartMode?.(modeId);
  };
  const proceedWithSprint = (familyId?: string) => {
    if (familyId && onStartMode) {
      onStartMode('sprint', {
        targetFamilyId: familyId
      });
      return;
    }
    setPendingModeId('sprint');
    setFamilyPickerMode('sprint');
    setFamilyPickerOpen(true);
  };
  const proceedWithFreePractice = (familyId?: string) => {
    if (familyId && onStartMode) {
      onStartMode('free', {
        targetFamilyId: familyId
      });
      return;
    }
    setPendingModeId('free');
    setFamilyPickerMode('free');
    setFamilyPickerOpen(true);
  };
  const proceedWithChallenge = (familyId?: string) => {
    if (familyId && onStartMode) {
      setPendingChallengeFamilyId(familyId);
      setChallengeModalOpen(true);
      return;
    }
    setPendingModeId('challenge');
    setFamilyPickerMode('challenge');
    setFamilyPickerOpen(true);
  };
  const handleChallengeIntroStart = () => {
    setChallengeIntroOpen(false);
    dataLayer.setChallengeIntroSeen(true);
    proceedWithChallenge();
  };
  const handleChallengeIntroCancel = () => {
    setChallengeIntroOpen(false);
  };
  const handleFamilySelect = (familyId: string) => {
    setFamilyPickerOpen(false);
    if (pendingModeId === 'challenge') {
      setPendingChallengeFamilyId(familyId);
      setChallengeModalOpen(true);
      setPendingModeId(null);
      return;
    }
    if (onStartMode && pendingModeId) {
      onStartMode(pendingModeId, {
        targetFamilyId: familyId
      });
    }
    setPendingModeId(null);
  };
  const handleFamilyPickerClose = () => {
    setFamilyPickerOpen(false);
    setPendingModeId(null);
  };
  const handleChallengeAccept = () => {
    setChallengeModalOpen(false);
    if (onStartMode && pendingChallengeFamilyId) {
      onStartMode('challenge', {
        targetFamilyId: pendingChallengeFamilyId
      });
    }
    setPendingChallengeFamilyId(null);
  };
  const handleChallengeDecline = () => {
    setChallengeModalOpen(false);
    setPendingChallengeFamilyId(null);
  };
  const handleFluencyStart = () => {
    setFluencyIntroOpen(false);
    dataLayer.setFluencyIntroSeen(true);
    if (pendingFluencyConfig?.familyId && onStartMode) {
      onStartMode('fluency', {
        targetFamilyId: pendingFluencyConfig.familyId
      });
    } else {
      setFluencyModulePickerOpen(true);
    }
    setPendingFluencyConfig(null);
  };
  const handleFluencyCancel = () => {
    setFluencyIntroOpen(false);
    setPendingFluencyConfig(null);
  };
  const handleSprintStart = () => {
    setSprintIntroOpen(false);
    dataLayer.setSprintIntroSeen(true);
    proceedWithSprint(pendingSprintConfig?.familyId);
    setPendingSprintConfig(null);
  };
  const handleSprintCancel = () => {
    setSprintIntroOpen(false);
    setPendingSprintConfig(null);
  };
  const handleFreePracticeStart = () => {
    setFreePracticeIntroOpen(false);
    dataLayer.setFreePracticeIntroSeen(true);
    proceedWithFreePractice(pendingFreePracticeConfig?.familyId);
    setPendingFreePracticeConfig(null);
  };
  const handleFreePracticeCancel = () => {
    setFreePracticeIntroOpen(false);
    setPendingFreePracticeConfig(null);
  };
  // ── Derived state ──
  const pendingChallengeMastery = pendingChallengeFamilyId ? state.familyMastery?.[getFamilyMasteryKey(pendingChallengeFamilyId, selectedGroup)]?.masteryScore ?? 0 : 0;
  // ── Fluency cold-start detection ──
  const isFluencyColdStart = useMemo(() => checkFluencyColdStart(state.familyMastery || {}, selectedGroup), [state.familyMastery, selectedGroup]);
  // ── FamilyPicker full-screen view ──
  if (familyPickerOpen) {
    return <FamilyPicker onClose={handleFamilyPickerClose} onSelect={handleFamilySelect} mode={familyPickerMode} data-id="element-2831" />;
  }
  // ── Main view ──
  return <PageContainer className="space-y-8 pb-20" data-id="element-2832">
      {/* Newly Unlocked Challenge Notification */}
      <AnimatePresence data-id="element-2833">
        {newlyUnlockedFamilyId && <motion.div initial={{
        opacity: 0,
        y: -20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -20,
        scale: 0.95
      }} transition={{
        type: 'spring',
        bounce: 0.4,
        duration: 0.6
      }} className="relative z-10" data-id="element-2834">
            <div className="bg-gradient-to-r from-amber-400 to-orange-500 p-1 rounded-2xl shadow-xl shadow-orange-500/20" data-id="element-2835">
              <div className="bg-white rounded-xl p-4 flex items-center gap-4 relative overflow-hidden" data-id="element-2836">
                <div className="absolute -right-4 -top-4 text-orange-100 opacity-50 transform rotate-12" data-id="element-2837">
                  <Trophy className="w-24 h-24" data-id="element-2838" />
                </div>

                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 shadow-sm" data-id="element-2839">
                  <Trophy className="w-6 h-6 text-orange-500" data-id="element-2840" />
                </div>

                <div className="flex-1 relative z-10" data-id="element-2841">
                  <div className="text-xs font-bold text-orange-500 uppercase tracking-wider mb-0.5 flex items-center gap-1" data-id="element-2842">
                    <Sparkles className="w-3 h-3" data-id="element-2843" />
                    Uitdaging ontgrendeld!
                  </div>
                  <div className="text-slate-800 font-medium text-sm md:text-base" data-id="element-2844">
                    Je hebt 70% gehaald voor{' '}
                    <span className="font-bold" data-id="element-2845">
                      {humanizeFamilyId(newlyUnlockedFamilyId)}
                    </span>
                    .
                  </div>
                </div>

                <button onClick={() => setNewlyUnlockedFamilyId(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors relative z-10" aria-label="Sluiten" data-id="element-2846">
                  <X className="w-5 h-5" data-id="element-2847" />
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Compact Hero + Mission Area */}
      <motion.div variants={itemVariants} data-id="element-2848">
        <div className="bg-violet-50/80 rounded-[2.5rem] p-6 sm:p-8 border-2 border-white shadow-lg" data-id="element-2849">
          {/* Header Row */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8" data-id="element-2850">
            <div className="flex items-center gap-4" data-id="element-2851">
              <div className="w-16 h-16 rounded-full bg-white shadow-md flex items-center justify-center flex-shrink-0" data-id="element-2852">
                <DjayAvatar size="sm" emotion={labWelcome.emotion} variant={selectedVariant} animate={false} data-id="element-2853" />
              </div>
              <div data-id="element-2854">
                <h1 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tight" data-id="element-2855">
                  Oefenlab
                </h1>
                <p className="text-slate-600 font-medium text-sm sm:text-base" data-id="element-2856">
                  {labWelcome.text}
                </p>
              </div>
            </div>

            <div className="sm:ml-auto flex-shrink-0" data-id="element-2857">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-sm border border-slate-100" data-id="element-2858">
                <Sparkles className="w-4 h-4 text-violet-500" data-id="element-2859" />
                <span className="text-sm font-bold text-slate-700" data-id="element-2860">
                  {allModes.length} manieren om te oefenen
                </span>
              </div>
            </div>
          </div>

          {/* Mission Briefing */}
          <MissionBriefing onModeClick={handleModeClick} data-id="element-2861" />
        </div>
      </motion.div>

      {/* Lab Stations Grid */}
      <motion.div variants={itemVariants} data-id="element-2862">
        <WorldZone icon={Beaker} title="Hoe wil je oefenen?" accentColor="bg-fuchsia-100 text-fuchsia-600" djayMessage="Kies een manier om te oefenen! 🧪" djayVariant={selectedVariant} djayEmotion="excited" isLast={true} data-id="element-2863">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2864">
            {allModes.map(mode => {
            const enrichment = getEnrichmentForMode(mode.id);
            return <motion.div key={mode.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2 + allModes.indexOf(mode) * 0.05
            }} data-id="element-2865">
                  <LabStation mode={mode} statLabel={enrichment.statLabel} masteryPercent={enrichment.masteryPercent} personalRecord={enrichment.personalRecord} onClick={() => handleModeClick(mode.id)} data-id="element-2866" />
                </motion.div>;
          })}
          </div>
        </WorldZone>
      </motion.div>

      {/* ChallengeAcceptModal */}
      {pendingChallengeFamilyId && <ChallengeAcceptModal isOpen={challengeModalOpen} familyId={pendingChallengeFamilyId} masteryScore={pendingChallengeMastery} onAccept={handleChallengeAccept} onDecline={handleChallengeDecline} data-id="element-2867" />}

      {/* FluencyIntro Modal */}
      <FluencyIntro isOpen={fluencyIntroOpen} onStart={handleFluencyStart} onCancel={handleFluencyCancel} isColdStart={isFluencyColdStart} data-id="element-2868" />

      {/* FluencyModulePicker Modal */}
      <FluencyModulePicker isOpen={fluencyModulePickerOpen} onSelectAll={() => {
      setFluencyModulePickerOpen(false);
      onStartMode?.('fluency');
    }} onSelectModule={moduleId => {
      setFluencyModulePickerOpen(false);
      onStartMode?.('fluency', {
        targetModules: [moduleId]
      });
    }} onCancel={() => setFluencyModulePickerOpen(false)} data-id="element-2869" />

      {/* SprintIntro Modal */}
      <SprintIntro isOpen={sprintIntroOpen} onStart={handleSprintStart} onCancel={handleSprintCancel} data-id="element-2870" />

      {/* FreePracticeIntro Modal */}
      <FreePracticeIntro isOpen={freePracticeIntroOpen} onStart={handleFreePracticeStart} onCancel={handleFreePracticeCancel} data-id="element-2871" />

      {/* ChallengeIntro Modal */}
      <ChallengeIntro isOpen={challengeIntroOpen} onStart={handleChallengeIntroStart} onCancel={handleChallengeIntroCancel} data-id="element-2872" />
    </PageContainer>;
}